<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useLayersStore } from '@/stores/layers'

import { Spinner } from '@/components/ui/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { fetchPlanetscopeMosaics } from '@/components/maps/basemaps'

const props = defineProps({
  orgId: {
    type: [Number, String],
    required: true
  },
  mapId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['change'])

const layersStore = useLayersStore()

const isLoading = ref(true)
const isInitialized = ref(false)
const mosaics = ref({ monthly: [], quarterly: [] })
const activeTab = ref('monthly')

const monthNames = [
  'January', 'February', 'March',
  'April', 'May', 'June',
  'July', 'August', 'September',
  'October', 'November', 'December'
]
const quarterNames = ['Q1', 'Q2', 'Q3', 'Q4']

// Monthly state
const monthlyAvailableMonthsByYear = ref({})
const monthlyYearOptions = ref([])
const monthlySelectedYear = ref(null)
const monthlySelectedMonth = ref(null)

// Quarterly state
const quarterlyAvailableQuartersByYear = ref({})
const quarterlyYearOptions = ref([])
const quarterlySelectedYear = ref(null)
const quarterlySelectedQuarter = ref(null)

const monthOptionsForSelectedYear = computed(() => {
  if (!monthlySelectedYear.value) return []
  const monthsSet = monthlyAvailableMonthsByYear.value[Number(monthlySelectedYear.value)]
  if (!monthsSet) return []
  return [...monthsSet].sort((a, b) => a - b).map(m => String(m))
})

const quarterOptionsForSelectedYear = computed(() => {
  if (!quarterlySelectedYear.value) return []
  const quartersSet = quarterlyAvailableQuartersByYear.value[Number(quarterlySelectedYear.value)]
  if (!quartersSet) return []
  return [...quartersSet].sort((a, b) => a - b).map(q => String(q))
})

const monthlySelectedMosaic = computed(() => {
  const selYear = Number(monthlySelectedYear.value)
  const selMonth = Number(monthlySelectedMonth.value)
  return mosaics.value.monthly.find(m => {
    const date = new Date(m.first_acquired)
    return date.getUTCFullYear() === selYear && (date.getUTCMonth() + 1) === selMonth
  }) || mosaics.value.monthly[0]
})

const quarterlySelectedMosaic = computed(() => {
  const selYear = Number(quarterlySelectedYear.value)
  const selQuarter = Number(quarterlySelectedQuarter.value)
  return mosaics.value.quarterly.find(m => {
    const date = new Date(m.first_acquired)
    const q = Math.floor(date.getUTCMonth() / 3) + 1
    return date.getUTCFullYear() === selYear && q === selQuarter
  }) || mosaics.value.quarterly[0]
})

const selectedMosaic = computed(() =>
  activeTab.value === 'monthly' ? monthlySelectedMosaic.value : quarterlySelectedMosaic.value
)

const initMonthlyData = () => {
  mosaics.value.monthly.forEach(m => {
    const date = new Date(m.first_acquired)
    const y = date.getUTCFullYear()
    const mo = date.getUTCMonth() + 1
    if (!monthlyAvailableMonthsByYear.value[y]) {
      monthlyAvailableMonthsByYear.value[y] = new Set()
    }
    monthlyAvailableMonthsByYear.value[y].add(mo)
  })

  const numericYears = Object.keys(monthlyAvailableMonthsByYear.value)
    .map(y => parseInt(y, 10))
    .sort((a, b) => a - b)
  monthlyYearOptions.value = numericYears.map(y => String(y))

  if (mosaics.value.monthly.length) {
    const defaultMosaic = mosaics.value.monthly[0]
    const defaultDate = new Date(defaultMosaic.first_acquired)
    monthlySelectedYear.value = String(defaultDate.getUTCFullYear())
    monthlySelectedMonth.value = String(defaultDate.getUTCMonth() + 1)
  }
}

const initQuarterlyData = () => {
  mosaics.value.quarterly.forEach(m => {
    const date = new Date(m.first_acquired)
    const y = date.getUTCFullYear()
    const q = Math.floor(date.getUTCMonth() / 3) + 1
    if (!quarterlyAvailableQuartersByYear.value[y]) {
      quarterlyAvailableQuartersByYear.value[y] = new Set()
    }
    quarterlyAvailableQuartersByYear.value[y].add(q)
  })

  const numericYears = Object.keys(quarterlyAvailableQuartersByYear.value)
    .map(y => parseInt(y, 10))
    .sort((a, b) => a - b)
  quarterlyYearOptions.value = numericYears.map(y => String(y))

  if (mosaics.value.quarterly.length) {
    const defaultMosaic = mosaics.value.quarterly[0]
    const defaultDate = new Date(defaultMosaic.first_acquired)
    quarterlySelectedYear.value = String(defaultDate.getUTCFullYear())
    quarterlySelectedQuarter.value = String(Math.floor(defaultDate.getUTCMonth() / 3) + 1)
  }
}

// Restore a per-map selection saved earlier this session.
const restoreSelection = () => {
  const stored = layersStore.getPlanetscopeSelection(props.mapId)
  if (!stored) return
  if (stored.tab) activeTab.value = stored.tab
  if (stored.tab === 'quarterly') {
    if (stored.year) quarterlySelectedYear.value = stored.year
    if (stored.quarter) quarterlySelectedQuarter.value = stored.quarter
  } else {
    if (stored.year) monthlySelectedYear.value = stored.year
    if (stored.month) monthlySelectedMonth.value = stored.month
  }
}

const currentSelection = () => {
  const name = selectedMosaic.value?.name
  return activeTab.value === 'monthly'
    ? { tab: 'monthly', year: monthlySelectedYear.value, month: monthlySelectedMonth.value, name }
    : { tab: 'quarterly', year: quarterlySelectedYear.value, quarter: quarterlySelectedQuarter.value, name }
}

const persistSelection = () => {
  layersStore.setPlanetscopeSelection({ layerId: props.mapId, selection: currentSelection() })
}

// BasemapStack owns the style; it renders the mosaic as a raster layer.
const applySelection = () => {
  if (!selectedMosaic.value) return
  emit('change', currentSelection())
}

watch([activeTab, monthlySelectedYear, monthlySelectedMonth, quarterlySelectedYear, quarterlySelectedQuarter], () => {
  if (!isInitialized.value) return
  persistSelection()
  applySelection()
})

onMounted(async () => {
  // Shared, per-org cached fetch — already newest-first, and the same request
  // BasemapStack used to resolve the default mosaic.
  mosaics.value = await fetchPlanetscopeMosaics(Number(props.orgId))
  isLoading.value = false
  if (!mosaics.value.monthly.length && !mosaics.value.quarterly.length) return

  initMonthlyData()
  initQuarterlyData()
  restoreSelection()
  isInitialized.value = true
  applySelection()
})
</script>

<template>
  <div>
    <div v-if="isLoading" class="flex items-center gap-2 py-2">
      <Spinner class="w-4 h-4" />
      <span class="text-sm text-muted-foreground">Loading mosaics...</span>
    </div>

    <div v-else-if="mosaics.monthly.length || mosaics.quarterly.length">
      <Tabs v-model="activeTab" class="w-full">
        <TabsList class="grid w-full grid-cols-2">
          <TabsTrigger value="monthly">Monthly</TabsTrigger>
          <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly">
          <div v-if="mosaics.monthly.length" class="space-y-2 mt-2">
            <div class="flex space-x-2">
              <Select
                v-if="monthlyYearOptions.length"
                v-model="monthlySelectedYear"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="year in monthlyYearOptions"
                      :key="year"
                      :value="year"
                    >
                      {{ year }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select
                v-if="monthOptionsForSelectedYear.length"
                v-model="monthlySelectedMonth"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="month in monthOptionsForSelectedYear"
                      :key="month"
                      :value="month"
                    >
                      {{ monthNames[Number(month) - 1] }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="quarterly">
          <div v-if="mosaics.quarterly.length" class="space-y-2 mt-2">
            <div class="flex space-x-2">
              <Select
                v-if="quarterlyYearOptions.length"
                v-model="quarterlySelectedYear"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="year in quarterlyYearOptions"
                      :key="year"
                      :value="year"
                    >
                      {{ year }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select
                v-if="quarterOptionsForSelectedYear.length"
                v-model="quarterlySelectedQuarter"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Quarter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="q in quarterOptionsForSelectedYear"
                      :key="q"
                      :value="q"
                    >
                      {{ quarterNames[Number(q) - 1] }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>

    <p v-else class="text-sm text-muted-foreground py-2">No PlanetScope mosaics available</p>
  </div>
</template>
